import { useState, useRef, useEffect } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Upload, 
  Download, 
  FileSpreadsheet, 
  Users, 
  Receipt, 
  ArrowUpDown,
  Check,
  AlertCircle,
  Loader2,
  FileText,
  FileInput,
  Landmark,
  Plus
} from "lucide-react";

const ImportExport = () => {
  const { getAuthHeaders, token } = useAuth();
  const fileInputRef = useRef(null);
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [importType, setImportType] = useState("costs");
  const [exportType, setExportType] = useState("costs");
  const [exportFormat, setExportFormat] = useState("csv");
  const [importResult, setImportResult] = useState(null);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [selectedBankAccount, setSelectedBankAccount] = useState("");

  // Fetch bank accounts for estratto conto import
  useEffect(() => {
    const fetchBankAccounts = async () => {
      try {
        const response = await axios.get(`${API}/bank-accounts`, getAuthHeaders());
        setBankAccounts(response.data);
      } catch (error) {
        console.error("Error fetching bank accounts:", error);
      }
    };
    fetchBankAccounts();
  }, []);

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate bank account selection for estratto conto
    if (importType === "estratto_conto" && !selectedBankAccount) {
      toast.error("Seleziona un conto bancario prima di importare l'estratto conto");
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    setImporting(true);
    setImportResult(null);

    const formData = new FormData();
    formData.append('file', file);

    // Build URL with bank account if estratto conto
    let url = `${API}/import/excel?import_type=${importType}`;
    if (importType === "estratto_conto" && selectedBankAccount) {
      url += `&bank_account_id=${selectedBankAccount}`;
    }

    try {
      const response = await axios.post(
        url,
        formData,
        {
          headers: {
            ...getAuthHeaders().headers,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      setImportResult({ success: true, message: response.data.message, count: response.data.count });
      toast.success(response.data.message);
    } catch (error) {
      setImportResult({ success: false, message: error.response?.data?.detail || "Errore durante l'importazione" });
      toast.error(error.response?.data?.detail || "Errore durante l'importazione");
    } finally {
      setImporting(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const response = await axios.get(
        `${API}/export/${exportType}?format=${exportFormat}`,
        {
          ...getAuthHeaders(),
          responseType: 'blob'
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${exportType}.${exportFormat === 'excel' ? 'xlsx' : 'csv'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success("Export completato");
    } catch (error) {
      toast.error("Errore durante l'export");
    } finally {
      setExporting(false);
    }
  };

  const importTypeOptions = [
    { value: "costs", label: "Costi Funerali", icon: Receipt },
    { value: "clients", label: "Clienti", icon: Users },
    { value: "movements", label: "Movimenti Cassa", icon: ArrowUpDown },
    { value: "fatture_attive", label: "Fatture Attive", icon: FileText },
    { value: "fatture_passive", label: "Fatture Passive", icon: FileInput },
    { value: "estratto_conto", label: "Estratto Conto Bancario", icon: Landmark }
  ];

  const exportTypeOptions = [
    { value: "costs", label: "Costi Funerali", icon: Receipt },
    { value: "clients", label: "Clienti", icon: Users },
    { value: "movements", label: "Movimenti Cassa", icon: ArrowUpDown },
    { value: "fatture_attive", label: "Fatture Attive", icon: FileText },
    { value: "fatture_passive", label: "Fatture Passive", icon: FileInput }
  ];

  return (
    <div className="animate-fade-in" data-testid="import-export-page">
      <div className="page-header">
        <h1 className="page-title">Import / Export</h1>
        <p className="page-subtitle">Gestione dati tramite file Excel e CSV</p>
      </div>

      <Tabs defaultValue="import" className="space-y-6">
        <TabsList className="bg-[#F5F5F7] p-1 rounded-xl">
          <TabsTrigger 
            value="import" 
            className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm px-6"
            data-testid="import-tab"
          >
            <Upload className="w-4 h-4 mr-2" />
            Import
          </TabsTrigger>
          <TabsTrigger 
            value="export"
            className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm px-6"
            data-testid="export-tab"
          >
            <Download className="w-4 h-4 mr-2" />
            Export
          </TabsTrigger>
        </TabsList>

        {/* Import Tab */}
        <TabsContent value="import">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="bg-white rounded-2xl border-0 shadow-[0_2px_12px_rgba(0,0,0,0.04)]">
              <CardHeader>
                <CardTitle className="text-lg font-semibold text-[#1D1D1F]">
                  Importa Dati
                </CardTitle>
                <CardDescription className="text-[#86868B]">
                  Carica file Excel (.xlsx) o CSV per importare dati
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-2">
                  <Label className="text-[#1D1D1F]">Tipo di Dati</Label>
                  <Select value={importType} onValueChange={setImportType}>
                    <SelectTrigger className="h-12 rounded-xl border-[#D2D2D7]" data-testid="import-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white rounded-xl border-0 shadow-lg">
                      {importTypeOptions.map(option => (
                        <SelectItem key={option.value} value={option.value}>
                          <span className="flex items-center gap-2">
                            <option.icon className="w-4 h-4 text-[#86868B]" />
                            {option.label}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Bank Account Select - only for estratto_conto */}
                {importType === "estratto_conto" && (
                  <div className="space-y-2">
                    <Label className="text-[#1D1D1F]">Conto Bancario di Destinazione *</Label>
                    <Select value={selectedBankAccount} onValueChange={setSelectedBankAccount}>
                      <SelectTrigger className="h-12 rounded-xl border-[#D2D2D7]" data-testid="bank-account-select">
                        <SelectValue placeholder="Seleziona il conto bancario..." />
                      </SelectTrigger>
                      <SelectContent className="bg-white rounded-xl border-0 shadow-lg">
                        {bankAccounts.length === 0 ? (
                          <div className="p-4 text-center text-[#86868B]">
                            <p className="text-sm">Nessun conto bancario configurato</p>
                            <a 
                              href="/conti-bancari" 
                              className="text-[#007AFF] text-sm mt-1 inline-flex items-center gap-1 hover:underline"
                            >
                              <Plus className="w-3 h-3" />
                              Crea un conto
                            </a>
                          </div>
                        ) : (
                          bankAccounts.map(account => (
                            <SelectItem key={account.id} value={account.id}>
                              <span className="flex items-center gap-2">
                                <div 
                                  className="w-3 h-3 rounded-full" 
                                  style={{ backgroundColor: account.color || '#3b82f6' }}
                                />
                                {account.name} ({account.bank_name})
                              </span>
                            </SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                    {!selectedBankAccount && (
                      <p className="text-xs text-[#FF9500]">
                        Seleziona un conto per associare i movimenti dell'estratto conto
                      </p>
                    )}
                  </div>
                )}

                <div 
                  className="border-2 border-dashed border-[#D2D2D7] rounded-2xl p-8 text-center hover:border-[#007AFF] hover:bg-[#007AFF]/5 transition-all cursor-pointer"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xlsx,.xls,.csv"
                    onChange={handleFileUpload}
                    className="hidden"
                    data-testid="file-input"
                  />
                  <div className="w-16 h-16 bg-[#007AFF]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    {importing ? (
                      <Loader2 className="w-8 h-8 text-[#007AFF] animate-spin" />
                    ) : (
                      <FileSpreadsheet className="w-8 h-8 text-[#007AFF]" />
                    )}
                  </div>
                  <p className="font-medium text-[#1D1D1F] mb-1">
                    {importing ? 'Importazione in corso...' : 'Clicca per caricare'}
                  </p>
                  <p className="text-sm text-[#86868B]">
                    Supportati: .xlsx, .xls, .csv
                  </p>
                </div>

                {importResult && (
                  <div className={`p-4 rounded-xl flex items-center gap-3 ${
                    importResult.success ? 'bg-[#34C759]/10' : 'bg-[#FF3B30]/10'
                  }`}>
                    {importResult.success ? (
                      <Check className="w-5 h-5 text-[#34C759]" />
                    ) : (
                      <AlertCircle className="w-5 h-5 text-[#FF3B30]" />
                    )}
                    <span className={importResult.success ? 'text-[#34C759]' : 'text-[#FF3B30]'}>
                      {importResult.message}
                    </span>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Import Instructions */}
            <Card className="bg-white rounded-2xl border-0 shadow-[0_2px_12px_rgba(0,0,0,0.04)]">
              <CardHeader>
                <CardTitle className="text-lg font-semibold text-[#1D1D1F]">
                  Formato File
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-3">
                  <h4 className="font-medium text-[#1D1D1F] flex items-center gap-2">
                    <Receipt className="w-4 h-4 text-[#007AFF]" />
                    Costi Funerali
                  </h4>
                  <div className="bg-[#F5F5F7] rounded-xl p-4 font-mono text-sm">
                    <p className="text-[#86868B]">Colonne richieste:</p>
                    <p className="text-[#1D1D1F]">descrizione, importo, categoria, data, note</p>
                  </div>
                </div>

                <div className="space-y-3">
                  <h4 className="font-medium text-[#1D1D1F] flex items-center gap-2">
                    <Users className="w-4 h-4 text-[#007AFF]" />
                    Clienti
                  </h4>
                  <div className="bg-[#F5F5F7] rounded-xl p-4 font-mono text-sm">
                    <p className="text-[#86868B]">Colonne richieste:</p>
                    <p className="text-[#1D1D1F]">nome, email, telefono, credito_totale, giorni_ritardo</p>
                  </div>
                </div>

                <div className="space-y-3">
                  <h4 className="font-medium text-[#1D1D1F] flex items-center gap-2">
                    <ArrowUpDown className="w-4 h-4 text-[#007AFF]" />
                    Movimenti Cassa
                  </h4>
                  <div className="bg-[#F5F5F7] rounded-xl p-4 font-mono text-sm">
                    <p className="text-[#86868B]">Colonne richieste:</p>
                    <p className="text-[#1D1D1F]">descrizione, importo, tipo, data, categoria</p>
                    <p className="text-[#86868B] text-xs mt-2">tipo: "income" o "expense"</p>
                  </div>
                </div>

                <div className="space-y-3">
                  <h4 className="font-medium text-[#1D1D1F] flex items-center gap-2">
                    <FileText className="w-4 h-4 text-[#34C759]" />
                    Fatture Attive
                  </h4>
                  <div className="bg-[#F5F5F7] rounded-xl p-4 font-mono text-sm">
                    <p className="text-[#86868B]">Colonne richieste:</p>
                    <p className="text-[#1D1D1F]">numero, cliente, descrizione, imponibile, iva, totale, data_emissione, data_scadenza, stato</p>
                    <p className="text-[#86868B] text-xs mt-2">stato: "emessa", "pagata", "scaduta"</p>
                  </div>
                </div>

                <div className="space-y-3">
                  <h4 className="font-medium text-[#1D1D1F] flex items-center gap-2">
                    <FileInput className="w-4 h-4 text-[#FF3B30]" />
                    Fatture Passive
                  </h4>
                  <div className="bg-[#F5F5F7] rounded-xl p-4 font-mono text-sm">
                    <p className="text-[#86868B]">Colonne richieste:</p>
                    <p className="text-[#1D1D1F]">numero, fornitore, descrizione, imponibile, iva, totale, data_ricezione, data_scadenza, stato</p>
                    <p className="text-[#86868B] text-xs mt-2">stato: "ricevuta", "pagata", "scaduta"</p>
                  </div>
                </div>

                <div className="space-y-3">
                  <h4 className="font-medium text-[#1D1D1F] flex items-center gap-2">
                    <Landmark className="w-4 h-4 text-[#007AFF]" />
                    Estratto Conto Bancario
                  </h4>
                  <div className="bg-[#F5F5F7] rounded-xl p-4 font-mono text-sm">
                    <p className="text-[#86868B]">Colonne supportate (flessibili):</p>
                    <p className="text-[#1D1D1F]">data, data_valuta, descrizione, dare, avere, saldo</p>
                    <p className="text-[#86868B] text-xs mt-2">oppure: data, importo, descrizione, saldo</p>
                    <p className="text-[#86868B] text-xs">Nomi colonna alternativi: Data, Dare, Avere, Descrizione, Saldo, amount, balance</p>
                  </div>
                  <div className="bg-[#007AFF]/5 rounded-xl p-3 text-sm text-[#007AFF] border border-[#007AFF]/20">
                    <p className="font-medium">Nota:</p>
                    <p>I movimenti bancari vengono automaticamente categorizzati e collegati al cashflow.</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Export Tab */}
        <TabsContent value="export">
          <Card className="bg-white rounded-2xl border-0 shadow-[0_2px_12px_rgba(0,0,0,0.04)] max-w-xl">
            <CardHeader>
              <CardTitle className="text-lg font-semibold text-[#1D1D1F]">
                Esporta Dati
              </CardTitle>
              <CardDescription className="text-[#86868B]">
                Scarica i tuoi dati in formato Excel o CSV
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label className="text-[#1D1D1F]">Tipo di Dati</Label>
                <Select value={exportType} onValueChange={setExportType}>
                  <SelectTrigger className="h-12 rounded-xl border-[#D2D2D7]" data-testid="export-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white rounded-xl border-0 shadow-lg">
                    {exportTypeOptions.map(option => (
                      <SelectItem key={option.value} value={option.value}>
                        <span className="flex items-center gap-2">
                          <option.icon className="w-4 h-4 text-[#86868B]" />
                          {option.label}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-[#1D1D1F]">Formato</Label>
                <Select value={exportFormat} onValueChange={setExportFormat}>
                  <SelectTrigger className="h-12 rounded-xl border-[#D2D2D7]" data-testid="export-format-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white rounded-xl border-0 shadow-lg">
                    <SelectItem value="csv">CSV (.csv)</SelectItem>
                    <SelectItem value="excel">Excel (.xlsx)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Button
                onClick={handleExport}
                disabled={exporting}
                className="w-full h-12 bg-[#007AFF] hover:bg-[#0071E3] text-white rounded-full"
                data-testid="export-btn"
              >
                {exporting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Esportazione...
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4 mr-2" />
                    Esporta {exportType === 'costs' ? 'Costi' : exportType === 'clients' ? 'Clienti' : 'Movimenti'}
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ImportExport;
